import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { API_BASE } from '../config';

// Live load tracking for the broker / customer on the rate confirmation.
// mytagalong.app/s/<token> — public, no sign-in. The token opens ONE load: where its
// truck is now, moving or stopped, when the GPS was read, the delivery city and ETA.
// Nothing about the driver or any other load. It stops working once the load is delivered.

const REFRESH_MS = 60000;
const fmt = (ms) => (ms ? new Date(ms).toLocaleString('en-US', { timeZone: 'America/New_York', weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : null);
const ago = (iso) => { const m = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60000)); return m < 1 ? 'just now' : m < 60 ? `${m} min ago` : `${Math.floor(m / 60)} h ${m % 60} min ago`; };
const tokenOf = () => (window.location.pathname.match(/^\/s\/([\w-]+)/) || [])[1] || '';

const S = {
  page: { minHeight: '100vh', background: '#F3F4F6', fontFamily: 'Arial, Helvetica, sans-serif', color: '#111827' },
  wrap: { maxWidth: 600, margin: '0 auto', padding: 16, boxSizing: 'border-box' },
  head: { background: '#1E3A5F', color: '#FFFFFF', padding: '16px 16px', fontSize: 18, fontWeight: 700 },
  card: { background: '#FFFFFF', border: '1px solid #D1D5DB', borderRadius: 8, padding: 18, marginTop: 14 },
  label: { fontSize: 13, color: '#4B5563', marginTop: 10 },
  value: { fontSize: 16, lineHeight: 1.5, wordBreak: 'break-word' },
  map: { height: 300, borderRadius: 8, overflow: 'hidden', border: '1px solid #D1D5DB', marginTop: 14, background: '#E5E7EB' },
  small: { fontSize: 13, color: '#4B5563', marginTop: 14, lineHeight: 1.4 },
};

function TruckMap({ position }) {
  const el = useRef(null);
  const map = useRef(null);
  const pin = useRef(null);
  useEffect(() => {
    if (!el.current || !position) return;
    try {
      if (!map.current) {
        map.current = L.map(el.current, { zoomControl: true, attributionControl: true }).setView([position.lat, position.lng], 10);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 18, attribution: '© OpenStreetMap' }).addTo(map.current);
      }
      const icon = L.divIcon({ className: '', html: '<div style="width:18px;height:18px;border-radius:50%;background:#1E3A5F;border:3px solid #fff;box-shadow:0 0 0 2px #1E3A5F"></div>', iconSize: [18, 18], iconAnchor: [9, 9] });
      if (pin.current) pin.current.setLatLng([position.lat, position.lng]); else pin.current = L.marker([position.lat, position.lng], { icon }).addTo(map.current);
      map.current.panTo([position.lat, position.lng]);
    } catch { /* map unavailable (old browser / test) — the text still shows where the truck is */ }
  }, [position]);
  useEffect(() => () => { try { if (map.current) map.current.remove(); } catch { /* gone */ } }, []);
  return <div ref={el} style={S.map} role="img" aria-label={position ? `Truck location near ${position.place || 'the map pin'}` : 'No location'} />;
}

export default function BrokerTrackPage() {
  const [view, setView] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let stop = false;
    const load = async () => {
      try {
        const r = await fetch(`${API_BASE}/track/${encodeURIComponent(tokenOf())}`);
        const j = await r.json();
        if (stop) return;
        if (!r.ok && !('active' in j)) { setErr(j.error || 'This tracking link is not valid.'); return; }
        setErr(''); setView(j);
      } catch { if (!stop) setErr('No connection — trying again shortly.'); }
    };
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => { stop = true; clearInterval(t); };
  }, []);

  const shell = (body) => <div style={S.page}><div style={S.head}><div style={{ maxWidth: 600, margin: '0 auto' }}>Florida Beauty Flora · Live load tracking</div></div><div style={S.wrap}>{body}</div></div>;
  if (err && !view) return shell(<div style={S.card}><div style={S.value}>{err}</div></div>);
  if (!view) return shell(<div style={S.small}>Loading…</div>);
  if (!view.active) {
    return shell(<div style={S.card}><div style={{ fontSize: 18, fontWeight: 700 }}>{view.ended === 'delivered' ? 'This load was delivered' : view.error ? 'Link not valid' : 'Tracking has ended'}</div><div style={{ ...S.value, marginTop: 6 }}>{view.error || `Tracking for trip ${view.trip} has ended. Contact Florida Beauty Flora dispatch for anything else about this load.`}</div></div>);
  }
  const p = view.position;
  const row = (k, v) => (v ? <><div style={S.label}>{k}</div><div style={S.value}>{v}</div></> : null);
  return shell(
    <>
      <div style={S.card}>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#1E3A5F' }}>Trip {view.trip}{view.loadNumber ? ` · Load ${view.loadNumber}` : ''}</div>
        <div style={{ display: 'inline-block', marginTop: 8, fontSize: 13, fontWeight: 700, color: '#1F2937', background: '#E5E7EB', border: '1px solid #9CA3AF', borderRadius: 4, padding: '2px 8px' }}>{view.status}</div>
        {row('Current location', p ? `${p.place || `${p.lat}, ${p.lng}`} · ${p.moving ? `moving${p.mph != null ? ` ${p.mph} mph` : ''}` : 'stopped'}` : 'Location being updated — check back shortly')}
        {row('Last GPS update', p ? `${fmt(Date.parse(p.at))} ET (${ago(p.at)})` : null)}
        {row('Delivering to', view.destination)}
        {row('Estimated arrival', view.etaMs ? `${fmt(view.etaMs)} ET${view.miles != null ? ` · about ${view.miles} mi to go` : ''}` : view.etaNote)}
        {row('Appointment', view.apptMs ? `${fmt(view.apptMs)} ET` : null)}
      </div>
      {p && <TruckMap position={p} />}
      <div style={S.small}>Updates every minute. This link shows only this load and stops working once it is delivered. All times Eastern (ET).</div>
    </>,
  );
}
